import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid } from "@mui/material";
import { Link } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

function PreviousYearQuestions() {
  const [items, setItems] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({});
  const [notification, setNotification] = useState({ open: false, color: "success", message: "" });

  const showMessage = (message, color = "success") => {
    setNotification({ open: true, color, message });
    setTimeout(() => setNotification({ open: false, color, message: "" }), 2500);
  };

  const loadItems = async (page = pageNo, currentLimit = limit) => {
    if (init() !== "success") return;
    try {
      const resp = await axios.get(
        `${Constants.getUrls.getAllPreviousYearQuestions}?page=${page}&limit=${currentLimit}`
      );
      setItems(resp?.data?.data?.docs || []);
      setPagination(resp?.data?.data || {});
      setPageNo(resp?.data?.data?.page || page);
      setLimit(resp?.data?.data?.limit || currentLimit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to load previous year questions.", "error");
    }
  };

  useEffect(() => {
    loadItems(1, limit);
  }, []);

  const handleDelete = async (id) => {
    try {
      const resp = await axios.delete(`${Constants.deleteUrls.deletePreviousYearQuestion}${id}`);
      showMessage(resp?.data?.message || "Previous year question deleted successfully.");
      loadItems(pageNo, limit);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to delete previous year question.", "error");
    }
  };

  const rows = items.map((item, index) => ({
    serial: (pageNo - 1) * limit + index + 1,
    exam: item?.exam_id?.exam_name || "-",
    year: item?.year_id?.year || "-",
    examList: item?.exam_list_id?.exam_name
      ? `${item.exam_list_id.exam_name}${item?.exam_list_id?.shift ? ` • ${item.exam_list_id.shift}` : ""}`
      : "-",
    setDetails: item?.exam_list_set_id
      ? `${item.exam_list_set_id.number_of_questions || 0} Q • ${item.exam_list_set_id.time_duration || 0} Min`
      : "-",
    pattern: item?.question_pattern
      ? String(item.question_pattern)
          .split("_")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      : "Mcq",
    question: (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium">
          {item?.question || "-"}
        </MDTypography>
        <MDTypography variant="caption" color="secondary" display="block">
          {item?.subject_id?.subject_name || "No subject"} {item?.topic_id?.topic_name ? `• ${item?.topic_id?.topic_name}` : ""}
        </MDTypography>
      </MDBox>
    ),
    answer: item?.answer || "-",
    action: (
      <MDBox display="flex" gap={1}>
        <Link to={`/update-previous-year-question/${item._id}`}>
          <MDButton variant="outlined" size="small" color="info">
            Update
          </MDButton>
        </Link>
        <MDButton
          variant="outlined"
          size="small"
          color="error"
          onClick={() => handleDelete(item._id)}
        >
          Delete
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">
              Previous Year Questions
            </MDTypography>
          </MDBox>
          <Grid item sm={4} px={2} py={2} align="right">
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <Link to="/add-bulk-previous-year-question">
                <MDButton variant="contained" size="small" color="info">
                  Bulk Import
                </MDButton>
              </Link>
              <Link to="/add-previous-year-question">
                <MDButton variant="contained" size="small" color="primary">
                  Create Question
                </MDButton>
              </Link>
            </MDBox>
          </Grid>
          <MDBox>
            <DataTable
              table={{
                columns: [
                  { Header: "serial", accessor: "serial", width: "6%" },
                  { Header: "Exam", accessor: "exam" },
                  { Header: "Year", accessor: "year" },
                  { Header: "Exam List", accessor: "examList" },
                  { Header: "Set", accessor: "setDetails" },
                  { Header: "Pattern", accessor: "pattern" },
                  { Header: "Question", accessor: "question" },
                  { Header: "Answer", accessor: "answer" },
                  { Header: "Action", accessor: "action" },
                ],
                rows,
              }}
              entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
              page_no={pagination?.page}
              totalPages={pagination?.totalPages}
              hasNextPage={pagination?.hasNextPage}
              hasPrevPage={pagination?.hasPrevPage}
              isnextPage={pagination?.nextPage}
              prevPage={pagination?.prevPage}
              totalDocs={pagination?.totalDocs}
              onPageChange={(page) => loadItems(page, limit)}
              onEntriesPerPageChange={(newLimit) => loadItems(1, newLimit)}
            />
          </MDBox>
          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default PreviousYearQuestions;
